import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, GradeBadge, FrameworkBadge } from "@/components/common";
import { useTimeOnTask } from "@/hooks/useTimeOnTask";
import { Markdown } from "@/components/Markdown";
import { TutorChat } from "@/components/TutorChat";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { ArrowLeft, Target, CheckCircle2, ListChecks, MessageCircleQuestion, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function LessonPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [tutorId, setTutorId] = useState(null);
  const [marking, setMarking] = useState(false);
  const navigate = useNavigate();
  useTimeOnTask("lesson", id);

  useEffect(() => {
    setLesson(null);
    setTutorId(null);
    api
      .get(`/lessons/${id}`)
      .then(({ data }) => {
        setLesson(data);
        return api.post("/tutor/sessions", { lesson_id: id });
      })
      .then(({ data }) => setTutorId(data.session_id))
      .catch((e) => {
        toast.error(errMsg(e));
        navigate("/catalog");
      });
  }, [id, navigate]);

  if (!lesson) return <Loading />;
  const idx = (lesson.outline || []).findIndex((o) => o.lesson_id === lesson.lesson_id);
  const nextLesson = idx >= 0 ? lesson.outline[idx + 1] : null;
  const completed = lesson.progress?.completed;
  const canDelete = user.role === "admin" || lesson.author_id === user.user_id;

  const markDone = async () => {
    setMarking(true);
    try {
      const { data } = await api.post(`/lessons/${id}/complete`);
      setLesson({ ...lesson, progress: { ...lesson.progress, completed: true } });
      toast.success(data.xp_gained ? `Lesson complete! +${data.xp_gained} XP` : "Lesson marked complete");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setMarking(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this lesson? This can't be undone.")) return;
    try {
      await api.delete(`/lessons/${id}`);
      toast.success("Lesson deleted");
      navigate(lesson.course ? `/courses/${lesson.course.course_id}` : "/create");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const tutor = tutorId ? <TutorChat sessionId={tutorId} compact lessonMode /> : <div className="p-6 text-sm text-muted-foreground">Starting tutor...</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
      <article className="lg:col-span-8 min-w-0">
        <Button variant="ghost" asChild className="mb-4 -ml-3">
          <Link to={lesson.course ? `/courses/${lesson.course.course_id}` : "/create"} data-testid="lesson-back-link">
            <ArrowLeft className="h-4 w-4 mr-1" /> {lesson.course ? lesson.course.title : "My lessons"}
          </Link>
        </Button>

        <div className="bg-white border border-border rounded-[18px] p-6 sm:p-10 fade-up">
          <div className="flex items-center gap-2 flex-wrap">
            <GradeBadge grade={lesson.grade_band} />
            <span className="text-sm text-muted-foreground">{lesson.subject}</span>
            {lesson.source === "ai" && <span className="text-xs bg-[#D7F3F1] text-[#0B5B5B] rounded-full px-2 py-0.5">AI-written</span>}
            {completed && <span className="text-xs bg-[#DDF7E7] text-[#14663A] rounded-full px-2 py-0.5 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Completed</span>}
            {canDelete && <Button variant="ghost" size="sm" className="ml-auto text-destructive" onClick={del} data-testid="lesson-delete-button"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>}
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl font-display font-semibold tracking-tight text-[#071A22]" data-testid="lesson-title">{lesson.title}</h1>
          <FrameworkBadge item={lesson} className="mt-3" />
          {lesson.summary && <p className="mt-3 text-lg text-muted-foreground max-w-[72ch]">{lesson.summary}</p>}

          {lesson.objectives?.length > 0 && (
            <div className="mt-8 bg-[#D7F3F1] rounded-[14px] p-5" data-testid="lesson-objectives">
              <div className="flex items-center gap-2 font-display font-semibold"><Target className="h-4 w-4 text-[#0B6B6B]" /> What you'll learn</div>
              <ul className="mt-3 space-y-2">
                {lesson.objectives.map((o, i) => <li key={i} className="flex gap-2 text-[#123645]"><CheckCircle2 className="h-4 w-4 mt-1 text-[#0F7B7D] shrink-0" />{o}</li>)}
              </ul>
              {lesson.standards?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#B3E4E0]" data-testid="lesson-standards">
                  <div className="text-xs font-display uppercase tracking-[0.14em] text-[#0B6B6B]/80 mb-2">
                    {lesson.collection && lesson.collection !== "Core" ? "Official framework objectives covered" : "Aligned to U.S. Dept. of Education standards"}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {lesson.standards.map((s, i) => (
                      <span key={i} className="max-w-full break-words px-2 py-0.5 rounded-md bg-white/70 border border-[#B3E4E0] text-xs font-mono text-[#0B6B6B]" data-testid="lesson-standard-chip">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {lesson.vocabulary?.length > 0 && (
            <div className="mt-6" data-testid="lesson-vocabulary">
              <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground mb-2">Key words · hover or tap</div>
              <div className="flex flex-wrap gap-2">
                {lesson.vocabulary.map((v) => (
                  <HoverCard key={v.term} openDelay={80}>
                    <HoverCardTrigger asChild>
                      <button className="px-3 py-1.5 rounded-full border border-border bg-[#FBFAF4] text-sm font-medium hover:bg-[#FFF1C7] transition-colors duration-150" data-testid="vocab-chip">{v.term}</button>
                    </HoverCardTrigger>
                    <HoverCardContent className="bg-white text-sm w-72"><div className="font-semibold mb-1">{v.term}</div>{v.definition}</HoverCardContent>
                  </HoverCard>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 space-y-10 max-w-[72ch]" data-testid="lesson-sections">
            {lesson.sections.map((s, i) => (
              <section key={i}>
                <h2 className="text-xl sm:text-2xl font-display font-semibold mb-4 text-[#071A22]">{s.heading}</h2>
                <Markdown className="prose-lesson text-[#123645] text-base">{s.body}</Markdown>
              </section>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row gap-3">
            {lesson.quiz?.length > 0 && (
              <Button className="h-12 px-6" onClick={() => navigate(`/lessons/${id}/quiz`)} data-testid="lesson-start-quiz-button">
                <ListChecks className="h-4 w-4 mr-2" /> Take the quiz ({lesson.quiz.length} questions)
              </Button>
            )}
            {!completed && (
              <Button variant="outline" className="h-12 bg-white" onClick={markDone} disabled={marking} data-testid="lesson-mark-complete-button">
                <CheckCircle2 className="h-4 w-4 mr-2" /> Mark as read
              </Button>
            )}
            {nextLesson && (
              <Button variant="ghost" className="h-12 sm:ml-auto" asChild data-testid="lesson-next-button">
                <Link to={`/lessons/${nextLesson.lesson_id}`}>Next: {nextLesson.title} <ChevronRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            )}
          </div>
        </div>
      </article>

      <aside className="hidden lg:block lg:col-span-4">
        <div className="sticky top-6 h-[calc(100vh-3rem)] border border-border rounded-[18px] overflow-hidden">{tutor}</div>
      </aside>

      <div className="lg:hidden fixed bottom-5 right-5 z-40">
        <Sheet>
          <SheetTrigger asChild>
            <Button className="h-14 rounded-full px-5 shadow-[var(--shadow-md)]" data-testid="lesson-open-tutor-button"><MessageCircleQuestion className="h-5 w-5 mr-2" />Ask Sage</Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="p-0 h-[85vh] bg-white">
            <SheetTitle className="sr-only">AI tutor</SheetTitle>
            <div className="h-full pt-8">{tutor}</div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
